// (c) Santosh Sahoo
import {
  filter,
  find,
  forEach,
  get,
  includes,
  isArray,
  isNil,
  isPlainObject,
  isString,
  keys,
  lowerCase,
  map,
  mapValues,
  set,
  some,
  trim,
  truncate as truncString,
  zipObject,
} from "lodash";

export type StringLike = string | null | undefined;

export type UUID = `${string}-${string}-${string}-${string}-${string}`;
export const slugify = slug;

export function isStringEmpty(str: StringLike): str is string {
  if (!str) return true;
  if (!String(str).trim()) return true;
  return false;
}

export function truncate(str: StringLike, length = 40) {
  if (!str) return "";
  return truncString(
    String(str)
      // biome-ignore lint/suspicious/noControlCharactersInRegex: required
      .replace(/[\x00-\x1F\x7F-\x9F]/g, "")
      .trim(),
    { length },
  );
}

export function formatStringTemplate(
  template: string, //'Hi {{ name || "Default Value" | upper }}'
  data: { [K: string]: unknown } = {},
) {
  if (!template) return "";
  return template.replace(
    /{{\s*([\w.]+)\s*(?:\|\s*([\w:]+))?\s*(?:\|\|\s*(?:(['"])(.*?)\3|([\w.]+)))?\s*}}/g,
    (_, key, filter, quotedDefault = "", unquotedDefault = "") => {
      const val: string = get(
        data,
        key,
        quotedDefault || unquotedDefault || "",
      );
      if (filter === "upper") return String(val).toUpperCase();
      if (filter === "lower") return String(val).toLowerCase();
      return String(val);
    },
  );
}

export function unslugify(slug: StringLike) {
  if (!slug) return slug;
  const result = slug.replace(/-/g, " ");
  return result.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase(),
  );
}

export function titleCase(str: StringLike) {
  if (!str) return "";
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

export function slug(text: StringLike) {
  if (!text) return "";
  return String(text)
    .normalize("NFKD") // normalize accented characters
    .replace(/[\u0300-\u036f]/g, "") // remove diacritics
    .trim() // trim whitespace
    .toLowerCase() // convert to lowercase
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
}

export const notEmpty = (str: StringLike): boolean => {
  return !!str && str.length > 0;
};

export function pad00(n: number): string {
  let t = Math.abs(n).toString();
  if (t.length < 2) {
    t = `0${t}`;
  }
  return n < 0 ? `-${t}` : t;
}

export function pad000(n: number): string {
  let t = Math.abs(n).toString();
  if (t.length < 2) {
    t = `00${t}`;
  }
  if (t.length < 3) {
    t = `0${t}`;
  }
  return n < 0 ? `-${t}` : t;
}

export const capitalize = (str: StringLike): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitalizeFirstLetter = (str: StringLike): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const makeGlobal = 0;

const picUrlRegex = /(imgix.net|\.jpe?g|\.png)/;

export const isNotVideo = (url?: string | null): boolean => {
  if (url && picUrlRegex.exec(url)) {
    return true;
  }
  return false;
};

export function isValidUrl(str: StringLike) {
  if (!str) return false;
  const pattern = /^(https?|ftp|mailto):\/\/[^\s/$.?#].[^\s]*$/i;
  return pattern.test(str);
}

export function fixLink(link: StringLike) {
  return isValidUrl(link) ? link : `//${link}`;
}

export const isHtmlFormatting = (str: StringLike): boolean => {
  if (!str) return false;
  const regex = /(<\/([^>]+)>)|&nbsp;/;
  if (str.match(regex)) {
    return true;
  }
  return false;
};

export function hashString(str: string, seed = 42) {
  if (!str) return 0;
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch: number; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

export const stringToMarkdown = (str: StringLike): string => {
  if (!str) return "";
  return str
    .replace(
      /([^\n]+)([\n])/g,
      (_match: string, p1: string, _p2: StringLike) => `${p1}#nl##nl#`,
    )
    .replace(/\n/g, "\u200b#nl##nl#")
    .replace(/#nl#/g, "\n");
};

export function isBase64(value: StringLike): boolean {
  if (value?.startsWith("data:")) return true;
  return false;
}

export const removeZeroWidthChar = (str: StringLike): string => {
  if (!str) return "";
  return str.replace(/[\u200B-\u200D\uFEFF]/g, "");
};

export function stripHtml(str: StringLike): string {
  if (!str) return "";
  const normalized = String(str)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/\s+/g, " ");
  return removeZeroWidthChar(normalized).trim();
}

export function isRichTextEmpty(str: StringLike): boolean {
  return !stripHtml(str);
}

export function removeLineBreaks(str: StringLike) {
  if (!str) return "";
  return str.replace(/[\n\r]+/g, " ");
}

export function addLineBR(str: StringLike) {
  if (!str) return "";
  return str.replace(/[\n\r]+/g, "<br/>");
}

export function formatNumber(str: StringLike) {
  if (!str) return "";
  if (Number.isNaN(str)) return str;
  const num = parseInt(str, 10);
  return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

export function abbrNumber(num, fixed = 0) {
  if (Number.isNaN(num)) return num;

  num = parseInt(num, 10);
  fixed = !fixed || fixed < 0 ? 0 : fixed; // number of decimal places to show
  const b = num.toPrecision(2).split("e"); // get power
  const k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3); // floor at decimals, ceiling at trillions
  const c =
    k < 1 ? num.toFixed(0 + fixed) : (num / 10 ** (k * 3)).toFixed(1 + fixed); // divide by power
  const d = c < 0 ? c : Math.abs(c); // enforce -0 is 0
  const e = d + ["", "K", "M", "B", "T"][k]; // append power
  return e;
}

export function formatCurrency(
  amount: number,
  currency = window ? window["currency"] : "USD",
) {
  if (Number.isNaN(amount)) return amount;

  const moneyFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
  return moneyFmt.format(amount);
}

export function abbrName(text: StringLike) {
  if (!text) return text;
  const letters = /^[A-Za-z]+$/;
  return text
    .split(" ")
    .map((partOfName) => (partOfName[0] !== "" ? partOfName.toUpperCase() : ""))
    .filter((name) => name.match(letters))
    .join(".");
}

export function wordCount(str: StringLike) {
  if (!str) return 0;
  return str.split(" ").filter(Boolean).length;
}

export function isEmpty(str: StringLike) {
  return !str || !str.trim();
}

export function formatYesNo(bool: boolean, no = "") {
  if (isNil(bool)) return "";
  return bool ? "Yes" : no;
}

export function formatCSV(arr: unknown[], sep = ",") {
  if (!Array.isArray(arr)) return arr;
  return arr.join(sep);
}

export function parseCSV(line: StringLike): string[] {
  if (!line) return [];
  const regex = /"(.*?)"|[^,]+/g;
  const matches = String(line).match(regex);
  if (!matches) return [];

  return matches.map((field) => {
    const trimmed = field.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return trimmed.slice(1, -1).replace(/""/g, '"');
    }
    return trimmed;
  });
}

export function parseLines(line: StringLike): string[] {
  if (!line) return [];
  return String(line)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function htmlToText(html: StringLike) {
  if (!html) return "";
  return String(html)
    .replace(/<\s*br[^>]?>/, "\n")
    .replace(/(<([^>]+)>)/g, "");
}

function escapeRegExp(str: StringLike) {
  if (!str) return "";
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function fuzzyMatch(pattern: StringLike, str: StringLike) {
  if (!str) return false;
  if (!pattern) return false;
  pattern =
    ".*" +
    pattern
      .split("")
      .map((l) => `${escapeRegExp(l)}.*`)
      .join("");
  const re = new RegExp(pattern);
  return re.test(str);
}

export function formatPost(text: string, options: object = {}) {
  const config = {
    hashtags: {
      pattern: /#(\w+)/g,
      url: "/tags/$1",
    },
    mentions: {
      pattern: /@(\w+)/g,
      url: "/$1",
    },
    bold: {
      pattern: /\*\*(.*?)\*\*/g,
      replace: "<strong>$1</strong>",
    },
    underline: {
      pattern: /__(.*?)__/g,
      replace: "<u>$1</u>",
    },
    ...options,
  };

  let convertedText = text;

  // Convert hashtags to links
  if (config.hashtags?.pattern) {
    const { pattern, url } = config.hashtags;
    convertedText = convertedText.replace(pattern, `<a href="${url}">#$1</a>`);
  }

  // Convert mentions to links
  if (config.mentions?.pattern) {
    const { pattern, url } = config.mentions;
    convertedText = convertedText.replace(pattern, `<a href="${url}">@$1</a>`);
  }

  // Apply other Markdown styles
  if (config.bold?.pattern) {
    const { pattern, replace } = config.bold;
    convertedText = convertedText.replace(pattern, replace);
  }

  if (config.underline?.pattern) {
    const { pattern, replace } = config.underline;
    convertedText = convertedText.replace(pattern, replace);
  }

  return convertedText;
}

export function parseHashTags(str: StringLike) {
  if (!str) return [];
  return str.match(/#\w+/g)?.map((t) => t);
}

export function calculateReadTime(text: string, speedWPM = 183) {
  if (!text) return 0;
  const minutes = text.split(" ").filter(Boolean).length / speedWPM;
  return Math.floor(minutes * 60);
}

export function isEmail(email?: StringLike) {
  if (!email) return false;
  const reEMAIL = /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/;
  return reEMAIL.test(email);
}

export function getEmailDomain(email?: StringLike) {
  if (!email) return "";
  const idx = email.lastIndexOf("@");
  return (idx > -1 ? email.slice(idx + 1) : "").trim().toLowerCase();
}

export function maskEmail(email: StringLike) {
  if (!email) return "";
  const atPos = email.indexOf("@");
  if (atPos > 2) {
    return `${email.substring(0, atPos)}@..`;
  }
  return email;
}

export function isNumeric(str: unknown): str is number {
  if (str === 0) return true;
  if (!str) return false;
  return (
    !Number.isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !Number.isNaN(parseFloat(str as string))
  ); // ...and ensure strings of whitespace fail
}

export function iifNaN<T>(str: unknown, defaultValue?: T) {
  if (str === undefined || str === null) return defaultValue;
  if (isNumeric(str)) return str;
  return defaultValue;
}

function shortString(s: StringLike, l: number, reverse = false) {
  if (!s) return s;
  const stop_chars = [" ", "/", "&"];
  const acceptable_shortness = l * 0.8;
  s = reverse ? s.split("").reverse().join("") : s;
  let short_s = "";

  for (let i = 0; i < l - 1; i++) {
    short_s += s[i];
    if (i >= acceptable_shortness && stop_chars.indexOf(s[i]) >= 0) {
      break;
    }
  }
  if (reverse) {
    return short_s.split("").reverse().join("");
  }
  return short_s;
}

export function truncateUrl(url: string, l = 40) {
  if (!url) return url;
  const chunk_l = l / 2;
  url = url.replace("http://", "").replace("https://", "");

  if (url.length <= l) {
    return url;
  }

  const start_chunk = shortString(url, chunk_l, false);
  const end_chunk = shortString(url, chunk_l, true);
  return `${start_chunk}..${end_chunk}`;
}

export function isPhoneNumber(str: StringLike) {
  if (!str) return false;
  return /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(str);
}

export function compareUUID(str1: string, str2: StringLike) {
  if (!str1 || !str2) return false;
  return str1.replaceAll("-", "") === str2.replaceAll("-", "");
}

// Anonymize IP addresses, works with IPv4 and IPv6
// taken from https://github.com/revington/truncate-ip
export function anonymizeIP(ip: StringLike): string {
  if (!ip) return "";
  if (ip.includes(".")) {
    const parts = ip.split(".");
    if (parts.length === 4) {
      return `${parts[0]} . ${parts[1]} . ******`;
    }
    return ip;
  }

  if (ip.includes(":")) {
    const parts = ip.split(":");
    if (parts.length >= 4) {
      return `${parts[0]} : ${parts[1]} : *******`;
    }
    return ip;
  }
  return ip;
}

export function objStr(obj: Record<string, unknown>) {
  let k: string,
    cls = "";
  for (k in obj) {
    if (obj[k]) {
      cls && (cls += " ");
      cls += k;
    }
  }
  return cls;
}

export function clsx(...args: unknown[]) {
  let i = 0,
    tmp: unknown,
    str = "";
  const len = args.length;
  for (; i < len; i++) {
    tmp = args[i];
    if (tmp && typeof tmp === "string") {
      str += (str && " ") + tmp;
    }
  }
  return str;
}

export function getPasswordStrength(password: StringLike) {
  if (!password) return 0;
  if (password.length < 8) return 0;
  let score = 50;
  if (password.length > 10) score += 10;
  if (password.length > 12) score += 10;
  if (password.match(/(?=.*[0-9])/)) score += 10;
  if (password.match(/(?=.*[!,%,&,@,#,$,^,*,?,_,~,<,>,])/)) score += 10;
  if (password.match(/(?=.*[a-z])/)) score += 10;
  return score;
}

export function isValidEmail(email: StringLike) {
  if (!email) return false;
  const reEMAIL = /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/;
  return reEMAIL.test(email);
}

export function get_email_domain(email: StringLike) {
  if (!email) return "";
  const idx = email.lastIndexOf("@");
  return (idx > -1 ? email.slice(idx + 1) : "").trim().toLowerCase();
}

export function getEmailName(email: StringLike) {
  if (!email) return "";
  const idx = email.lastIndexOf("@");
  return (idx > -1 ? email.slice(0, idx) : "").replace(/[._-]/gi, " ");
}

export function getInitials(name: StringLike) {
  if (!name) return "";
  const parts = name.match(/\b\w/g) || [];
  return ((parts.shift() || "") + (parts.pop() || "")).toUpperCase();
}

export function ordinalNumber(n: number) {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function getRelativePath(path = "", proto = location.protocol) {
  return `${proto}//${location.host}${path}`;
}

const GUID_REGEX =
  /^{?[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}}?$/;

//01234567-89ab-cdef-0000-000000000000:36 //UUID
//018f690e247670009f465db61249a4a1:32 //without dashes
//dllXFUK2ROekWTKFUc7Mtg:22 //base62

export function isUUID(str?: unknown): str is UUID {
  if (!str) return false;
  return GUID_REGEX.test(String(str));
}

export function guid(): UUID {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return (s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4()) as UUID;
}

export function tryParseJson<T = unknown>(
  str: string | undefined,
  defaultValue?: T,
): T | undefined {
  if (!str) return defaultValue;
  try {
    return JSON.parse(str) as T;
  } catch {
    return defaultValue;
  }
}

export function shortId(length = 11) {
  return Array.from({ length }, () =>
    Math.floor(Math.random() * 36).toString(36),
  ).join("");
}

export function getFileExtension(filename: StringLike) {
  if (!filename) return "";
  return ((filename || "").split(".").pop() || "").toLowerCase();
}

export function getFileName(path: StringLike) {
  if (!path) return "";
  let base = path.split(/[\\/]/).reverse()[0];
  if (base.lastIndexOf(".") !== -1)
    base = base.substring(0, base.lastIndexOf("."));
  return base;
}

export function getFileNameFromDate(date: Date | string) {
  date = new Date(date);
  const yyyy = date.getFullYear().toString();
  const mm = (date.getMonth() + 1).toString(); // getMonth() is zero-based
  const dd = date.getDate().toString();
  return (
    (mm[1] ? mm : "0" + mm[0]) + "." + (dd[1] ? dd : "0" + dd[0]) + "." + yyyy
  );
}

export function getFileSizeHuman(fileSizeInBytes: number) {
  if (!fileSizeInBytes) return "";
  let i = -1;
  const byteUnits = [" KB", " MB", " GB", " TB", "PB", "EB", "ZB", "YB"];
  do {
    fileSizeInBytes = fileSizeInBytes / 1024;
    i++;
  } while (fileSizeInBytes > 1024);
  return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
}

export function parseFileName(filename: StringLike) {
  const ext = getFileExtension(filename);
  const name = getFileName(filename);
  return {
    ext,
    filename,
    basename: name,
    name: `${name}.${ext}`,
    uniqueName: `${shortId()}.${ext}`,
    title: capitalize(name.replace(/([^A-Za-z0-9]+)/g, " ")).trim(),
  };
}

export function searchByText(objects = [], properties = [], query = "") {
  if (!query) return objects || [];
  const searchSpecificProperties = Array.isArray(properties);
  return filter(objects, (c) => {
    const cProperties = searchSpecificProperties ? properties : keys(c);
    return find(cProperties, (property) => {
      if (c[property]) {
        return includes(lowerCase(c[property]), lowerCase(query));
      }
    });
  });
}

export async function linkPreview(link: StringLike) {
  const response = await fetch(`https://api.microlink.io/?url=${link}`);
  const { status, data } = (await response.json()) as any;
  if (status === "success") {
    return data;
  }
}

export function annonymizeEmail(email: StringLike) {
  if (!email) return;
  return email.replace(/(\w{3})[\w.-]+@([\w.]+\w)/, "$1***@$2");
}

export function arrayMove(array: [], index = 0, delta = 1) {
  const newIndex = index + delta;
  if (newIndex < 0 || newIndex === array.length) return; //Already at the top or bottom.
  const indexes = [index, newIndex].sort((a, b) => a - b); //Sort the indixes (fixed)
  array.splice(indexes[0], 2, array[indexes[1]], array[indexes[0]]); //Replace from lowest
  return newIndex;
}

export function hexToBase62(hex: StringLike): string {
  if (!hex) return "";
  const buffer = Buffer.from(hex, "hex");
  return buffer.toString("base64url");
}

/**
 * Convert unicode to ascii
 * @param {string} unicode string
 * @returns ascii string
 */
export function U2A(str: StringLike) {
  if (!str) return "";
  // biome-ignore lint/suspicious/noControlCharactersInRegex: required
  return str.replace(/[^\x00-\x7F]/g, "");
}

export function semverCompare(local: string, remote: StringLike) {
  if (!local || !remote || local.length === 0 || remote.length === 0) {
    return false;
  }
  if (local === remote) return true;

  const VPAT = /^\d+(\.\d+){0,2}$/;
  if (VPAT.test(local) && VPAT.test(remote)) {
    const lparts = local.split(".");
    while (lparts.length < 3) lparts.push("0");
    const rparts = remote.split(".");
    while (rparts.length < 3) rparts.push("0");
    for (let i = 0; i < 3; i++) {
      const l = parseInt(lparts[i], 10);
      const r = parseInt(rparts[i], 10);
      if (l === r) continue;
      return l > r;
    }
    return true;
  } else {
    return local >= remote;
  }
}

export function cleanUserInputText(str: StringLike) {
  if (!str) return str;
  return str.replace(/[^\x20-\x7E]+/g, "");
}

export function basicPage(html: string, title: string, head = "") {
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>${title}</title>
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
			* { margin: 0; }
			html, body { min-height: 100%; }
			body {
				font-family: system-ui, sans-serif;
				background-color: #fff;
				color: #333;
			}
			.page_center{
				height: 100dvh;
				display: flex;
				align-items: center;
				justify-content: center;
				flex-direction: column;
				padding:0 24px;
				line-height:1.5;
			}
    </style>
		${head}
</head>
<body>
   ${html}
</body>
</html>
`;
}

export function flashMessage(
  title: string,
  { message = "", redirect = "", action = "Home", heading = "" } = {},
) {
  return basicPage(
    `<div class="page_center">
		${heading}
    <h1>${title}</h1>
    <p>${message}</p>
    ${redirect ? `<a href="${redirect}">${action}</a>` : ""}
</div>`,
    title,
  );
}

export function parseBearerToken(authHeader: string) {
  if (!authHeader) return undefined;
  const CREDENTIALS_REGEXP = /^ *(?:ctoken|[bB]earer) +(.*)$/;

  const match = CREDENTIALS_REGEXP.exec(authHeader);
  if (!match) return undefined;

  return match[1];
}

export function optionByValue<T = unknown>(
  list: { label: string; value: T }[],
) {
  return (value: T) => list.find((i) => i.value === value);
}

export function labelFromOptions<T = unknown>(
  list: { label: string; value: T }[],
  defaultValue?: string,
) {
  return (value: T) =>
    list.find((i) => i.value === value)?.label ?? defaultValue;
}

export function meters2miles(meters: number) {
  if (Number.isNaN(meters)) return meters;
  return Math.round(meters * 0.000621371192);
}

export function cleanObj<T>(o: T, ...omitFields: string[]): Partial<T> {
  if (isNil(o) || typeof o !== "object") {
    return o;
  }

  if (Array.isArray(o)) {
    o.map((i) => cleanObj(i, ...omitFields));
  }

  for (const key in o) {
    if (isNil(o[key])) {
      delete o[key];
    } else if (
      [
        "passwd",
        "salt",
        "is_deleted",
        "company_id",
        "tenant_id",
        "created_by",
        "modified_by",
        "prompt",
        "__COUNT",
        "__ROWNUM",
        "ip",
        "data",
        "fts",
        ...omitFields,
      ].indexOf(key) > -1
    ) {
      delete o[key];
    } else {
      cleanObj(o[key], ...omitFields);
    }
  }
  return o;
}

export function cleanMap<T = unknown>(
  list: T[],
  picker = (x: T) => x as Partial<T>,
  omitFields: string[] = [],
) {
  return map(list, (x) => cleanObj(picker(x), ...omitFields));
}

export class ValidationError extends Error {
  code?: number;
  constructor(message: string | { error: string; code: number }) {
    if (typeof message === "string") {
      super(message);
    } else {
      super(message.error);
      this.code = message.code;
    }
    this.name = "ValidationError";
  }
}

export function getOS(agent: StringLike) {
  if (!agent) return "";
  if (/ipad|iphone|ipod/.test(agent)) return "iOS";
  if (/mac/.test(agent)) return "Mac";
  if (/android/.test(agent)) return "Android";
  if (/linux/.test(agent)) return "Linux";
  if (/win/.test(agent)) return "Windows";
}

export function getBrowserName(agent: StringLike) {
  if (!agent) return "";
  if (agent.indexOf("edge") > -1) return "Edge";
  if (agent.indexOf("edg") > -1) return "Chromium";
  if (agent.indexOf("opr") > -1) return "Opera";
  if (agent.indexOf("chrome") > -1) return "Chrome";
  if (agent.indexOf("trident") > -1) return "Ie";
  if (agent.indexOf("firefox") > -1) return "Firefox";
  if (agent.indexOf("safari") > -1) return "Safari";
}

export function getFriendlyBrowserName(agent: StringLike) {
  return `${getBrowserName(agent) || "unknown"}/${getOS(agent) || "unknown"}`;
}

export function classifyUserAgent(userAgent: StringLike) {
  if (!userAgent) return undefined;
  if (userAgent.match(/Android/i)) return 3;
  if (userAgent.match(/iPhone|iPad|iPod/i)) return 2;
  if (
    /iP(?:hone|ad|od)|BlackBerry|Palm|Googlebot-Mobile|Mobile|mobile|mobi|Windows Mobile|Safari Mobile|Android|Opera (?:Mini|Mobi)/i.test(
      userAgent,
    )
  ) {
    return 1;
  }
  return 0;
}

export function flattenObject(
  obj: any,
  concatenator = ".",
): Record<string, unknown> {
  return Object.keys(obj).reduce((acc, key) => {
    if (typeof obj[key] !== "object" || !obj[key]) {
      return {
        ...acc,
        [key]: obj[key],
      };
    }
    const flattenedChild = flattenObject(obj[key], concatenator);
    return {
      ...acc,
      ...Object.keys(flattenedChild).reduce(
        (childAcc, childKey) => ({
          ...childAcc,
          [`${key}${concatenator}${childKey}`]: flattenedChild[childKey],
        }),
        {},
      ),
    };
  }, {});
}

export function normalizeEmail(email: StringLike) {
  if (!email) return "";
  return String(email).trim().toLocaleLowerCase();
}

export function normalizeUrl(url: StringLike) {
  if (!url) return "";
  return String(url).trim().toLocaleLowerCase();
}

export function isValidExt(filename: string, extensions: string[]) {
  if (!filename) return false;
  let fileExt = filename.split(".").pop();
  if (!fileExt) return false;
  fileExt = fileExt.toLowerCase();
  for (let i = 0, ext = ""; i < extensions.length; i++) {
    ext = extensions[i];
    if (fileExt === ext) {
      return true;
    }
  }
  return false;
}

const RE_DOMAIN_NAME = /^(?:https?:\/\/)?(?:[^@/\n]+@)?([^:/\n]+)/;
export function parseDomainName(url: StringLike) {
  if (!url) return "";
  const match = url.match(RE_DOMAIN_NAME);
  if (match) {
    return match[1];
  }
}

export function parseUrl(url?: StringLike) {
  if (!url) return {};
  const u = new URL(url.startsWith("http") ? url : `https://${url}`);
  return {
    host: u.host,
    authority: u.hostname,
    fragment: u.hash,
    path: u.pathname,
    query: u.search,
    scheme: u.protocol,
  };
}

export function isValidApiKey(apiKey: StringLike) {
  if (!apiKey) return false;
  if (/^[ps]k_([A-Za-z0-9_-]){32}$/.test(apiKey)) return true;
  return false;
}

export function includesSome<T = unknown>(arr1: T[], arr2: T[]) {
  return some(arr1, (x) => includes(arr2, x));
}

export function slugify_title(title?: string, MAX_LENGTH = 50) {
  if (!title) return guid();
  return `${slug((title || "u").substring(0, MAX_LENGTH - 6))}-${shortId(6)}`;
}

export function deepPick(input: any, paths: string[] | string) {
  if (!input || !paths) return {};

  const pathList = Array.isArray(paths) ? paths : [paths];

  function extractValue(obj: any, pathSegments: string[]): any {
    if (pathSegments.length === 0) return obj;

    const [segment, ...rest] = pathSegments;

    const isArraySegment = segment.endsWith("[]");
    const key = isArraySegment ? segment.slice(0, -2) : segment;

    const value = get(obj, key);

    if (isArraySegment) {
      if (!Array.isArray(value)) return [];

      return value
        .map((item) => extractValue(item, rest))
        .filter((v) => !isNil(v));
    } else {
      if (isNil(value)) return undefined;
      return extractValue(value, rest);
    }
  }

  return pathList.reduce((result, path) => {
    const pathSegments = path.split(".");
    const value = extractValue(input, pathSegments);

    if (!isNil(value)) {
      set(result, path.replace(/\[\]/g, ""), value); // strip [] for final path
    }

    return result;
  }, {});
}

export function getDownloadURL(key?: string, suffix = "", base = "/download") {
  if (!key) return undefined;
  if (key.startsWith("https://")) return key;
  if (key.startsWith("data:")) return key;
  return `${base}/${key}${suffix}`;
}

// Convert dot notation object to nested object
export function unflattenObject(obj: Object) {
  const result = {};
  for (const objectPath in obj) {
    const parts = objectPath.split(".");
    let target = result;
    while (parts.length > 1) {
      const part = parts.shift()!;
      target = target[part] = target[part] || {};
    }
    target[parts[0]] = obj[objectPath];
  }
  return result;
}

export function kvToObject(kv?: Array<{ key: string; value: unknown }>) {
  if (!kv) return {};
  return zipObject(
    map(kv, "key"),
    map(kv, (kv) => tryParseJson(String(kv.value))),
  ) as Record<string, unknown>;
}

export function wildcardStringMatch(str: StringLike, rule: string) {
  if (!str) return false;
  return new RegExp(`^${rule.split("*").join(".*")}$`).test(str);
}

export function compareArray(a: unknown[], b: unknown[]) {
  const unionSize = new Set([...a, ...b]).size;
  return unionSize === a.length && unionSize === b.length;
}

export function cleanCsv(str: StringLike) {
  if (!str) return [];
  return str
    .split(",")
    .filter(Boolean)
    .filter((f) => f.trim());
}

export function csv2arr(str: StringLike, delimiter = ",") {
  if (!str) return [];
  const lines = String(str).split("\n");
  return lines.map((l) => l.split(delimiter).map((s) => s.trim()));
}

export function lqipJpg(base64: StringLike) {
  if (!base64) return base64;
  return `data:image/jpeg;base64,${base64}`;
}

export function list2Options(list: string[]) {
  return map(list, (label, value) => ({ label, value }));
}

export function itemFromOptions<T extends Record<string, unknown>, V = string>(
  list: T[],
  valueKey = "value",
) {
  return (value: V) => list.find((i) => i[valueKey] === value);
}

type Col = {
  format?: (x: unknown) => unknown;
  name: string;
  label: string;
};

export function renderFormField(col: Col, row: unknown) {
  const val = get(row, col.name || "");
  return col.format ? col.format(val) : val;
}

/* const condition1 = 'A = 10 AND B <> 5';
const condition2 = 'C > 20 OR D <= 100';
const condition3 = 'A = 10 AND (C < 30 OR D > 50)';
const target = {
	A: 10,
	B: 4,
	C: 25,
	D: 90
};
 */
export function evaluateCondition(
  condition: string,
  target: Record<string, unknown>,
) {
  condition = condition
    .replace(/\bTRUE\b/g, "true")
    .replace(/\bFALSE\b/g, "false")
    .replace(/\bAND\b/g, "&&")
    .replace(/\bOR\b/g, "||")
    .replace(/\bNOT\b/g, "!")
    .replace(/<>/g, "!=")
    .replace(/(\w+)\s*(=|!=|>|<|>=|<=)\s*(\d+)/g, 'target["$1"] $2 $3')
    .replace(/(\w+)\s*=\s*(\w+|\d+|TRUE|FALSE)/g, 'target["$1"] == $2'); // Treat "=" as "=="

  const func = new Function("target", `return ${condition};`);

  try {
    return func(target);
  } catch (e) {
    console.error("Error evaluating condition:", e);
    return false;
  }
}

export function updateArray<T>(
  array: T[],
  value: T,
  isChecked = !array.includes(value),
) {
  if (isChecked) {
    if (!array.includes(value)) {
      return [...array, value];
    }
  } else {
    return array.filter((item) => item !== value);
  }
  return array;
}

export const StatusColors = {
  0: "#DBEAFE", // blue-100 info
  1: "#FEF3C7", // yellow-100 warning
  2: "#FECACA", // red-100 error
  3: "#D1FAE5", // green-100 success
  4: "#F3F4F6", // gray-100 inactive
  5: "#EDE9FE", // purple-100 custom
  6: "#BBF7D0", // green-200 active
  7: "#FEF9C3", // yellow-200 pending
  8: "#FECACA", // red-100 terminated
  9: "#DDD6FE", // violet-200 probationary
  10: "#C7D2FE", // indigo-200 onLeave
  11: "#E5E7EB", // gray-200 retired
  12: "#FDE68A", // amber-200 contractor
  13: "#FBCFE8", // pink-200 temporary
  14: "#A5F3FC", // cyan-200 partTime
  15: "#BFDBFE", // blue-200 fullTime
  16: "#E0F2FE", // light blue-100 notStarted
};

export function generateMetaTags(options: {
  title?: string;
  description: string;
  keywords?: string;
  author?: string;
  robots?: string;
  viewport?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterSite?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  themeColor?: string;
  ga4?: string;
}) {
  const metaTags: string[] = [];

  // Title tag (not a meta tag, but important for SEO)
  if (options.title) {
    metaTags.push(`<title>${options.title}</title>`);
  }
  if (options.ga4) {
    metaTags.push(`<script async src="https://www.googletagmanager.com/gtag/js?id=${options.ga4}"></script>
      <script>
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${options.ga4}');
    </script>`);
  }

  // Standard meta tags
  const standardTags = [
    { name: "description", content: options.description },
    { name: "keywords", content: options.keywords },
    { name: "author", content: options.author },
    { name: "robots", content: options.robots || "index, follow" },
    { name: "theme-color", content: options.themeColor },
    {
      name: "viewport",
      content: options.viewport || "width=device-width, initial-scale=1",
    },
  ];

  standardTags.forEach((tag) => {
    if (tag.content) {
      metaTags.push(`<meta name="${tag.name}" content="${tag.content}">`);
    }
  });

  // Open Graph meta tags
  const ogTags = [
    { property: "og:title", content: options.ogTitle || options.title },
    {
      property: "og:description",
      content: options.ogDescription || options.description,
    },
    { property: "og:type", content: options.ogType || "website" },
    { property: "og:url", content: options.ogUrl },
    { property: "og:image", content: options.ogImage },
  ];

  ogTags.forEach((tag) => {
    if (tag.content) {
      metaTags.push(
        `<meta property="${tag.property}" content="${tag.content}">`,
      );
    }
  });

  // Twitter Card meta tags
  const twitterTags = [
    {
      name: "twitter:card",
      content: options.twitterCard || "summary_large_image",
    },
    { name: "twitter:site", content: options.twitterSite },
    { name: "twitter:title", content: options.twitterTitle || options.title },
    {
      name: "twitter:description",
      content: options.twitterDescription || options.description,
    },
    { name: "twitter:image", content: options.twitterImage || options.ogImage },
  ];

  forEach(twitterTags, (tag) => {
    if (tag.content) {
      metaTags.push(`<meta name="${tag.name}" content="${tag.content}">`);
    }
  });

  return metaTags.join("\n");
}

export function getFavicon(url?: StringLike) {
  if (!url || url.trim() === "") return;
  const cleanurl = url.replace(/^https?:\/\//, "");
  if (cleanurl) {
    return `https://www.google.com/s2/favicons?domain=${url}&sz=128`;
  }
}

export function countWords(text: StringLike): number {
  if (!text) return 0;
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

export function makeHtmlTable(columns: Col[], rows: unknown[]) {
  return `<table>
	<thead>
		<tr>
			${columns.map((c) => `<th>${c.label}</th>`).join("")}
		</tr>
	</thead>
	<tbody>
		${rows
      .map(
        (r) => `<tr>
				${columns.map((c) => `<td>${renderFormField(c, r)}</td>`).join("")}
			</tr>`,
      )
      .join("")}
	</tbody>
</table>`;
}

export function encodeB62UUID(input: UUID) {
  return Buffer.from(input.replace(/-/g, ""), "hex").toString("base64url");
}

export function decodeB62UUID(base64: string) {
  try {
    const hex = Buffer.from(base64, "base64url").toString("hex");
    const uuid = hex;
    if (isUUID(uuid)) return uuid as UUID;
  } catch {
    return null;
  }
}

export function trimStringsInObject<T extends Record<string, any>>(obj: T): T {
  const result = mapValues(obj, (value) => {
    if (isString(value)) {
      return trim(value);
    } else if (isPlainObject(value)) {
      return trimStringsInObject(value);
    } else if (isArray(value)) {
      return value.map((item: string) =>
        isString(item)
          ? trim(item)
          : isPlainObject(item)
            ? trimStringsInObject(item)
            : item,
      );
    }
    return value;
  });

  return result as T;
}

export function microTimeAgo(date: Date | string) {
  if (!date) return "";
  date = new Date(date);
  const ms = Date.now() - date.getTime();
  const ams = Math.abs(ms);

  const mins = Math.floor(ams / 60000);
  const hours = Math.floor(ams / 3600000);
  const days = Math.round(ams / 86400000);
  const weeks = Math.round(ams / 604800000);
  const years = Math.round(ams / 31104000000);

  if (mins < 60) return `${mins}m`;
  else if (hours < 24) return `${hours}h`;
  else if (days > 365) return `${years} y`;
  else if (days > 6 && days < 30) return `${weeks}w`;
  return `${days}d`;
}

export function timeAgoShort(
  date: Date | number | string,
  locale = "en",
  now = Date.now(),
): string {
  const past = new Date(date).getTime();
  const diffSeconds = Math.floor((now - past) / 1000);

  if (diffSeconds <= 0) return "0s";

  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: "always",
    style: "narrow",
  });

  const UNITS = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "week", seconds: 604800 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];
  for (const { unit, seconds } of UNITS) {
    const value = Math.floor(diffSeconds / seconds);
    if (value >= 1) {
      return rtf.format(-value, unit as Intl.RelativeTimeFormatUnit);
    }
  }

  return "0s";
}

export function formatDate(dt: Date | string) {
  if (!dt) return dt;
  const date = new Date(dt);
  const from = new Date();

  const isThisYear = from.getFullYear() === date.getFullYear();
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    ...(isThisYear ? {} : { year: "numeric" }),
  }).format(date);
}

export function addHours(hrs: number, to = new Date()) {
  return new Date(to.getTime() + hrs * 60000);
}

export function hashColor(str: string, { s = 70, l = 50 } = {}) {
  if (!str) return "hsl(0,0,0)";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, ${s}%, ${l}%)`;
}
